import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import API from '../lib/api';
import { Users, Target, TrendingUp, MapPin, ArrowLeft, Swords, BarChart3 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from 'recharts';

export default function PlayerProfile() {
    const { playerId } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        API.get(`/players/${playerId}`).then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
    }, [playerId]);

    if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" /></div>;
    if (!data) return <div className="text-white/40 text-center py-12">Player not found</div>;

    const { player, team, recent_form, form_trend, matchups, venue_performance } = data;

    const matchupChart = matchups.filter(m => m.played > 0).map(m => ({
        name: m.opponent.short_name,
        win_rate: m.win_rate,
        played: m.played,
        color: m.opponent.color,
    }));

    return (
        <div className="space-y-6" data-testid="player-profile">
            <Link to="/players" className="inline-flex items-center gap-2 text-white/40 hover:text-white text-sm transition-colors">
                <ArrowLeft className="w-4 h-4" /> Back to Players
            </Link>

            {/* Player Header */}
            <div className="glass-card p-6" data-testid="player-header">
                <div className="flex items-start gap-4 flex-wrap">
                    <div className="w-16 h-16 rounded-xl flex items-center justify-center text-2xl font-bold text-white" style={{ backgroundColor: team?.color || '#333' }}>
                        {player.name?.charAt(0)}
                    </div>
                    <div className="flex-1 min-w-[200px]">
                        <h1 className="heading-primary text-xl md:text-2xl text-white">{player.name}</h1>
                        <div className="flex flex-wrap items-center gap-3 mt-1">
                            <span className="text-sm px-2 py-0.5 rounded" style={{ backgroundColor: (team?.color || '#333') + '20', color: team?.color }}>{team?.name}</span>
                            <span className="tech-mono text-xs text-white/40">{player.role}</span>
                            <span className="tech-mono text-xs text-cyan-400">Impact: {player.impact_score}</span>
                        </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4 mt-2 md:mt-0">
                        <div className="text-center p-3 bg-white/5 rounded-lg">
                            <div className="text-xs text-white/30">FORM</div>
                            <div className={`heading-primary text-xl ${player.form === 'Excellent' || player.form === 'Good' ? 'text-green-400' : 'text-yellow-400'}`}>{player.form}</div>
                        </div>
                        <div className="text-center p-3 bg-white/5 rounded-lg">
                            <div className="text-xs text-white/30">RECENT</div>
                            <div className="heading-primary text-xl text-white">{recent_form.wins}W-{recent_form.losses}L</div>
                        </div>
                        <div className="text-center p-3 bg-white/5 rounded-lg">
                            <div className="text-xs text-white/30">MATCHES</div>
                            <div className="heading-primary text-xl text-white">{data.total_team_matches}</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {player.role !== 'Bowler' && (
                    <>
                        <StatBox label="BATTING AVG" value={player.batting_avg || '-'} icon={Target} accent="cyan" />
                        <StatBox label="STRIKE RATE" value={player.strike_rate || '-'} icon={TrendingUp} accent="green" />
                    </>
                )}
                {player.role !== 'Batsman' && (
                    <>
                        <StatBox label="BOWLING AVG" value={player.bowling_avg || '-'} icon={Target} accent="purple" />
                        <StatBox label="ECONOMY" value={player.economy || '-'} icon={BarChart3} accent="yellow" />
                    </>
                )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Matchup Intelligence */}
                <div className="glass-card p-6" data-testid="matchup-intelligence">
                    <div className="flex items-center gap-2 mb-4">
                        <Swords className="w-4 h-4 text-cyan-400" />
                        <h3 className="heading-primary text-sm text-white">MATCHUP INTELLIGENCE</h3>
                    </div>
                    {matchupChart.length > 0 ? (
                        <ResponsiveContainer width="100%" height={280}>
                            <BarChart data={matchupChart}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#222" />
                                <XAxis dataKey="name" tick={{ fill: '#999', fontSize: 11 }} />
                                <YAxis domain={[0, 100]} tick={{ fill: '#666', fontSize: 10 }} />
                                <Tooltip contentStyle={{ background: '#000', border: '1px solid #333', borderRadius: 8 }} labelStyle={{ color: '#fff' }}
                                    formatter={(val, name) => [`${val}%`, 'Team Win Rate']} />
                                <Bar dataKey="win_rate" radius={[4, 4, 0, 0]}>
                                    {matchupChart.map((entry, i) => (
                                        <Cell key={i} fill={entry.color} opacity={0.7} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    ) : (
                        <div className="text-white/30 text-center py-8">No matchup data available</div>
                    )}
                    <div className="space-y-2 mt-3 max-h-60 overflow-y-auto">
                        {matchups.filter(m => m.played > 0).map((m, i) => (
                            <div key={i} className="flex items-center justify-between p-2 bg-white/5 rounded-lg text-sm">
                                <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: m.opponent.color }} />
                                    <span className="text-white">{m.opponent.short_name}</span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <span className="text-white/40 text-xs">{m.played} matches</span>
                                    <span className="font-bold text-green-400">{m.team_wins}W</span>
                                    <span className="text-white/20">-</span>
                                    <span className="font-bold text-red-400">{m.team_losses}L</span>
                                    <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${m.win_rate >= 60 ? 'bg-green-500/10 text-green-400' : m.win_rate >= 40 ? 'bg-yellow-500/10 text-yellow-400' : 'bg-red-500/10 text-red-400'}`}>
                                        {m.win_rate}%
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Venue Performance */}
                <div className="glass-card p-6" data-testid="venue-performance">
                    <div className="flex items-center gap-2 mb-4">
                        <MapPin className="w-4 h-4 text-green-400" />
                        <h3 className="heading-primary text-sm text-white">VENUE PERFORMANCE</h3>
                    </div>
                    <div className="space-y-2 max-h-[400px] overflow-y-auto">
                        {venue_performance.map((v, i) => (
                            <div key={i} className="p-3 bg-white/5 rounded-lg">
                                <div className="flex items-center justify-between mb-1">
                                    <span className="text-sm text-white truncate flex-1">{v.venue_name}</span>
                                    <span className={`text-xs font-bold ml-2 ${v.win_rate >= 60 ? 'text-green-400' : v.win_rate >= 40 ? 'text-yellow-400' : 'text-red-400'}`}>
                                        {v.win_rate}%
                                    </span>
                                </div>
                                <div className="flex items-center gap-2 text-xs text-white/40">
                                    <span>{v.played} matches</span>
                                    <span className="text-green-400">{v.won}W</span>
                                    <span className="text-red-400">{v.played - v.won}L</span>
                                </div>
                                <div className="mt-1.5 h-1.5 bg-white/10 rounded-full overflow-hidden">
                                    <div className="h-full rounded-full bg-gradient-to-r from-green-600 to-green-400" style={{ width: `${v.win_rate}%` }} />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Form Trend */}
            {form_trend.length > 0 && (
                <div className="glass-card p-6" data-testid="form-trend">
                    <div className="flex items-center gap-2 mb-4">
                        <TrendingUp className="w-4 h-4 text-yellow-400" />
                        <h3 className="heading-primary text-sm text-white">RECENT FORM TREND</h3>
                    </div>
                    <div className="flex gap-2 overflow-x-auto pb-2">
                        {form_trend.map((f, i) => (
                            <div key={i} className={`flex-shrink-0 p-3 rounded-lg text-center min-w-[80px] border ${f.result === 'W' ? 'bg-green-500/10 border-green-500/20' : 'bg-red-500/10 border-red-500/20'}`}>
                                <div className={`heading-primary text-lg ${f.result === 'W' ? 'text-green-400' : 'text-red-400'}`}>{f.result}</div>
                                <div className="text-xs text-white/60">vs {f.opponent}</div>
                                {f.score && <div className="tech-mono text-[10px] text-white/30 mt-1">{f.score}</div>}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

function StatBox({ label, value, icon: Icon, accent }) {
    const colors = { cyan: 'text-cyan-400', green: 'text-green-400', purple: 'text-purple-400', yellow: 'text-yellow-400' };
    return (
        <div className="glass-card p-4 flex items-center gap-3">
            <Icon className={`w-5 h-5 ${colors[accent]}`} />
            <div>
                <div className="tech-mono text-[10px] text-white/30">{label}</div>
                <div className="heading-primary text-xl text-white">{value}</div>
            </div>
        </div>
    );
}
